import { createContext, useContext, useEffect, useState, useCallback } from 'react';

import { canonicalPersonaSlug, getPersona, PERSONAS, PERSONA_SLUGS } from '@/data/personas';
import { getIntent, INTENTS, INTENT_SLUGS } from '@/data/intentTags';

// ============================================================
// PersonaContext — Sprint 8 (2026-05-26).
//
// État global léger sans back-end. Trois dimensions superposables :
//   1. personaSlug   — qui est le visiteur (retraite/entrepreneur/...)
//   2. intentSlug    — quand part-il (move-soon/planning/exploring)
//   3. petOwner      — tag booléen transversal
//
// Persisté en localStorage sous 3 clés séparées (pour pouvoir
// retirer une dimension sans toucher les autres). Les valeurs sont
// validées au chargement (canonicalizer) ; tout slug inconnu = null.
//
// Aucun export du context lui-même — passer par usePersona().
// ============================================================

const KEY_PERSONA = 'ip.personaSlug';
const KEY_INTENT  = 'ip.intentSlug';
const KEY_PET     = 'ip.petOwner';

const PersonaContext = createContext(null);

function readStorage(key, validator) {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw == null) return null;
    if (validator) return validator(raw);
    return raw;
  } catch {
    return null;
  }
}

function writeStorage(key, value) {
  if (typeof window === 'undefined') return;
  try {
    if (value == null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, String(value));
  } catch {
    // ignore (Safari private mode, quota)
  }
}

export function PersonaProvider({ children }) {
  // Lazy initialise from localStorage so SSR/CSR don't desync.
  const [personaSlug, setPersonaState] = useState(() =>
    readStorage(KEY_PERSONA, canonicalPersonaSlug),
  );
  const [intentSlug, setIntentState] = useState(() => {
    const v = readStorage(KEY_INTENT);
    return INTENT_SLUGS.includes(v) ? v : null;
  });
  const [petOwner, setPetOwnerState] = useState(() => {
    const v = readStorage(KEY_PET);
    return v === '1' || v === 'true';
  });

  // Persist on change.
  useEffect(() => writeStorage(KEY_PERSONA, personaSlug), [personaSlug]);
  useEffect(() => writeStorage(KEY_INTENT, intentSlug),   [intentSlug]);
  useEffect(() => writeStorage(KEY_PET, petOwner ? '1' : null), [petOwner]);

  const setPersona = useCallback((slug) => {
    setPersonaState(canonicalPersonaSlug(slug));
  }, []);
  const setIntent = useCallback((slug) => {
    setIntentState(INTENT_SLUGS.includes(slug) ? slug : null);
  }, []);
  const setPetOwner = useCallback((bool) => {
    setPetOwnerState(!!bool);
  }, []);
  const reset = useCallback(() => {
    setPersonaState(null);
    setIntentState(null);
    setPetOwnerState(false);
  }, []);

  // Cross-tab sync : if the user opens 2 tabs and quizzes in one,
  // the other tab should reflect the new persona on next focus.
  useEffect(() => {
    function onStorage(e) {
      if (e.key === KEY_PERSONA) setPersonaState(canonicalPersonaSlug(e.newValue));
      else if (e.key === KEY_INTENT) setIntentState(INTENT_SLUGS.includes(e.newValue) ? e.newValue : null);
      else if (e.key === KEY_PET) setPetOwnerState(e.newValue === '1' || e.newValue === 'true');
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const persona = getPersona(personaSlug);
  const intent = getIntent(intentSlug);

  const value = {
    personaSlug, persona, setPersona,
    intentSlug,  intent,  setIntent,
    petOwner,    setPetOwner,
    reset,
    // Convenience flags for components that just want "is the visitor known?"
    hasPersona: !!persona,
    hasIntent:  !!intent,
  };

  return <PersonaContext.Provider value={value}>{children}</PersonaContext.Provider>;
}

/**
 * usePersona — main hook. Always returns a defined object (the provider
 * defaults everything to null/false). Components can safely destructure.
 *
 * @example
 *   const { persona, setPersona, petOwner } = usePersona();
 *   if (persona?.slug === 'retraite') ...
 */
export function usePersona() {
  const ctx = useContext(PersonaContext);
  if (!ctx) {
    // Provider absent — return a noop shape so components don't crash.
    // Useful during a partial rollout (Storybook, isolated tests).
    return {
      personaSlug: null, persona: null, setPersona: () => {},
      intentSlug: null,  intent: null,  setIntent: () => {},
      petOwner: false,   setPetOwner: () => {},
      reset: () => {},
      hasPersona: false, hasIntent: false,
    };
  }
  return ctx;
}

/**
 * Helper : applique l'accent persona en CSS variable sur un élément.
 * À utiliser sur un wrapper de section / card pour personnaliser couleurs.
 *
 * @example
 *   <div style={usePersonaAccentStyle()}> ... </div>
 *   // → --accent: #0F5350 (océan profond) si retraité, etc.
 */
export function usePersonaAccentStyle() {
  const { persona } = usePersona();
  if (!persona) return undefined;
  return { '--accent': persona.accentHex };
}
